import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';

import CreateProjectDropdown from '../../../components/DropdownStyle/CreateProjectDropdown';
import Select from '../../../components/ReactSelectDropdown';
import { filterOption } from '../../../components/ReactSelectDropdown/ReactSelectDropdown';
import SearchWithButton from '../../../components/Search/SearchWithButton';
import {
  FilterLabel,
  InputWithLabelSection,
} from '../../../styledComponents/common';
import { FilterContainer } from '../../../styledComponents/tasks';

const EmtithalOfficeTasksFilter = ({
  publicZoneList,
  getOfficesShortList,
  onFilterChange,
  officesShortList,
  getTaskLocation,
  locationList,
}) => {
  const { t } = useTranslation();
  const [selectedPublicZone, setSelectedPublicZone] = useState({
    label: t('all_public_zones'),
    value: null,
  });
  const [selectedOfficeLocal, setSelectedOfficeLocal] = useState({
    label: t('all_offices'),
    value: null,
  });
  const [selectedLocation, setSelectedLocation] = useState({
    label: t('all_locations'),
    value: null,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [officeOptions, setOfficeOptions] = useState([
    { label: t('all_offices'), value: null },
  ]);
  const [publicZoneOptions, setPublicZoneOptions] = useState([
    { label: t('all_public_zones'), value: null },
  ]);
  const [locationOptions, setLocationOptions] = useState([
    { label: t('all_locations'), value: null },
  ]);

  useEffect(() => {
    if (publicZoneList && publicZoneList.length > 0) {
      setPublicZoneOptions([
        { label: t('all_public_zones'), value: null },
        ...publicZoneList.map(zone => ({ label: zone.name, value: zone.id })),
      ]);
    }
  }, [publicZoneList, t]);

  useEffect(() => {
    if (selectedPublicZone?.value) {
      getOfficesShortList({ public_zone_id: selectedPublicZone.value });
    } else {
      getOfficesShortList();
    }
    setSelectedOfficeLocal({ label: t('all_offices'), value: null });
    setSelectedLocation({ label: t('all_locations'), value: null });
  }, [selectedPublicZone, getOfficesShortList, t]);

  useEffect(() => {
    if (selectedOfficeLocal?.value) {
      getTaskLocation({ office_id: selectedOfficeLocal.value });
    } else {
      getTaskLocation();
    }
  }, [selectedOfficeLocal, getTaskLocation]);

  useEffect(() => {
    if (officesShortList && officesShortList.length > 0) {
      const newArr = [
        { label: t('all_offices'), value: null },
        ...officesShortList.map(office => ({
          label: office.name,
          value: office.id,
        })),
      ];
      setOfficeOptions(newArr);
      if (selectedOfficeLocal?.value) {
        const exists = newArr.some(o => o.value === selectedOfficeLocal.value);
        if (!exists) {
          setSelectedOfficeLocal({ label: t('all_offices'), value: null });
        }
      }
    }
  }, [officesShortList, t]);

  useEffect(() => {
    if (locationList && locationList.length > 0) {
      setLocationOptions([
        { label: t('all_locations'), value: null },
        ...locationList.map(loc => ({
          label: loc.name,
          value: loc.id,
        })),
      ]);
    } else {
      setLocationOptions([{ label: t('all_locations'), value: null }]);
    }
  }, [locationList, t]);

  const getFilterState = () => ({
    publicZone: selectedPublicZone,
    office: selectedOfficeLocal,
    location: selectedLocation,
    searchTerm,
  });

  const updateFilters = filters => {
    if (onFilterChange) onFilterChange(filters);
  };

  const debouncedUpdateSearch = useDebouncedCallback(filters => {
    updateFilters(filters);
  }, 400);

  const handlePublicZoneChange = zone => {
    setSelectedPublicZone(zone);
    updateFilters({
      ...getFilterState(),
      publicZone: zone,
      office: { label: t('all_offices'), value: null },
      location: { label: t('all_locations'), value: null },
    });
  };

  const handleOfficeChange = office => {
    setSelectedOfficeLocal(office);
    setSelectedLocation({ label: t('all_locations'), value: null });
    updateFilters({
      ...getFilterState(),
      office,
      location: { label: t('all_locations'), value: null },
    });
  };

  const handleLocationChange = location => {
    setSelectedLocation(location);
    updateFilters({ ...getFilterState(), location });
  };

  const handleSearchChange = value => {
    setSearchTerm(value);
    debouncedUpdateSearch({ ...getFilterState(), searchTerm: value });
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    debouncedUpdateSearch.cancel();
    updateFilters({ ...getFilterState(), searchTerm: '' });
  };

  return (
    <FilterContainer>
      <InputWithLabelSection width="200px">
        <FilterLabel>{t('public_zone')}</FilterLabel>
        <Select
          isSearchable
          value={selectedPublicZone}
          options={publicZoneOptions}
          placeholder={`${t('select_public_zone')}...`}
          onChange={handlePublicZoneChange}
          styles={CreateProjectDropdown()}
          filterOption={filterOption}
        />
      </InputWithLabelSection>
      <SearchWithButton
        itemName={t('emtithal_tasks')}
        label={t('search_by_emtithal_tasks')}
        searchTermName={t('emtithal_tasks')}
        searchInput={searchTerm}
        onInputChange={e => handleSearchChange(e.target.value)}
        handleSearch={() => updateFilters(getFilterState())}
        handleClearSearch={handleClearSearch}
      />
      <InputWithLabelSection width="200px">
        <FilterLabel>{t('office')}</FilterLabel>
        <Select
          isSearchable
          value={selectedOfficeLocal}
          options={officeOptions}
          placeholder={`${t('select_office')}...`}
          onChange={handleOfficeChange}
          styles={CreateProjectDropdown()}
          filterOption={filterOption}
        />
      </InputWithLabelSection>
      {/* Location filter commented out for now
      <InputWithLabelSection width="200px">
        <FilterLabel>{t('location')}</FilterLabel>
        <Select
          isSearchable
          value={selectedLocation}
          options={locationOptions}
          placeholder={`${t('select_location')}...`}
          onChange={handleLocationChange}
          styles={CreateProjectDropdown()}
          filterOption={filterOption}
        />
      </InputWithLabelSection>
      */}
    </FilterContainer>
  );
};

export default EmtithalOfficeTasksFilter;
